import React, { useState, useContext, useEffect } from "react";
import {
  Modal,
  Form,
  Select,
  Input,
  InputNumber,
  Button,
  message,
  Spin,
} from "antd";
import { platformApi } from "../../../api";
import { ContextAPI } from "../../../ContextAPI";

const { Option } = Select;

const SpareAdjustmentForm = ({ form, open, close, refreshData, selectedData, editable }) => {
  const { loginCredintials } = useContext(ContextAPI);
  const [loading, setLoading] = useState(false);
  const [parts, setParts] = useState([]);
  const [fetchingParts, setFetchingParts] = useState(false);
  const [availableQty, setAvailableQty] = useState(0);

  const branches = loginCredintials?.branch || [];

  useEffect(()=>{
    handlePartSearch("");
  },[])

  useEffect(() => {
    if (selectedData && Object.keys(selectedData).length > 0 && open) {
      setAvailableQty(selectedData.availableQuantity);
      form.setFieldsValue({
        branch: selectedData.branch?.id,
        partNo: selectedData.partNo?.id,
        availableQuantity: selectedData.availableQuantity,
        newQuantity: selectedData.newQuantity,
        quantityAdjusted: (selectedData.newQuantity || 0) - (selectedData.availableQuantity || 0),
        reason: selectedData.reason,
      });
      // Ensure parts list contains the selected part so it shows up
      if (selectedData.partNo) {
        setParts([selectedData.partNo]);
      }
    } else {
      form.resetFields();
      setAvailableQty(0);
    }
  }, [selectedData, open]);

  const handlePartSearch = async (value) => {
    setFetchingParts(true);
    try {
      const payload = {
        module: "partsMasters",
        column: "partNumber",
        searchString: value,
        fields: ["partNumber", "partName"],
        size: 100,
        searchColumns: ["partNumber"],
      };
      const response = await platformApi.post("/api/options/get", payload);
      if (response.data.code === 200) {
        setParts(response.data.response || []);
      }
    } catch (error) {
      console.error("Error fetching parts:", error);
    } finally {
      setFetchingParts(false);
    }
  };

  const handlePartChange = async (partId) => {
    if (!partId) {
      setAvailableQty(0);
      form.setFieldsValue({ availableQuantity: 0, quantityAdjusted: 0 });
      return;
    }

    setLoading(true);
    try {
      const response = await platformApi.post("/api/sparesInventory/getPart", {
        partNo: partId,
        branch: form.getFieldValue("branch"),
        skipNull: false,
      });
      if (response.data.code === 200) {
        const inventory = response.data.response?.data?.sparesInventory?.[0];
        const phyQty = inventory?.phyQuantity || 0;
        setAvailableQty(phyQty);
        form.setFieldsValue({
          availableQuantity: phyQty,
        });
        const currentNewQty = form.getFieldValue("newQuantity");
        if (currentNewQty !== undefined && currentNewQty !== null) {
          form.setFieldsValue({ quantityAdjusted: currentNewQty - phyQty });
        }
      }
    } catch (error) {
      console.error("Error fetching part details:", error);
      message.error("Failed to fetch part inventory");
    } finally {
      setLoading(false);
    }
  };

  const calculateAdjusted = (newQty) => {
    if (newQty === undefined || newQty === null) return;
    const adjusted = newQty - availableQty;
    form.setFieldsValue({ quantityAdjusted: adjusted });
  };

  const handleSubmit = () => {
    form.validateFields(async (err, values) => {
      if (!err) {
        setLoading(true);
        try {
          const payload = {
            branch: values.branch,
            partNo: values.partNo,
            availableQuantity: values.availableQuantity,
            newQuantity: values.newQuantity,
            reason: values.reason,
          };

          const response = await platformApi.post(
            "/api/AdjustSparesInventory/create",
            payload
          );
          if (response.data.code === 200) {
            message.success("Spares adjustment created successfully");
            refreshData();
            close();
          } else {
            message.error(response.data.message || "Failed to create adjustment");
          }
        } catch (error) {
          console.error("Error creating adjustment:", error);
          message.error("An error occurred while creating the adjustment");
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const { getFieldDecorator } = form;

  return (
    <Modal
      title={editable ? "Add Spare Adjustment" : "View Spare Adjustment"}
      visible={open}
      onCancel={close}
      footer={null}
      width={600}
    >
      <Spin spinning={loading}>
        <Form layout="vertical">
          <Form.Item label="Branch">
            {getFieldDecorator("branch", {
              rules: [{ required: true, message: "Please select branch" }],
            })(
              <Select placeholder="Select Branch" disabled={!editable}>
                {branches.map((b) => (
                  <Option key={b.id} value={b.id}>
                    {b.name}
                  </Option>
                ))}
              </Select>
            )}
          </Form.Item>

          <Form.Item label="Select Part">
            {getFieldDecorator("partNo", {
              rules: [{ required: true, message: "Please select a part" }],
            })(
              <Select
                showSearch
                placeholder="Search by Part Number"
                filterOption={false}
                onSearch={handlePartSearch}
                onChange={handlePartChange}
                disabled={!editable}
                notFoundContent={fetchingParts ? <Spin size="small" /> : null}
              >
                {parts.map((p) => (
                  <Option key={p.id} value={p.id}>
                    {`${p.partNumber} - ${p.partName}`}
                  </Option>
                ))}
              </Select>
            )}
          </Form.Item>

          <Form.Item label="Quantity Available">
            {getFieldDecorator("availableQuantity", {
              initialValue: 0,
            })(<InputNumber style={{ width: "100%" }} disabled />)}
          </Form.Item>

          <Form.Item label="New Quantity in Hand">
            {getFieldDecorator("newQuantity", {
              rules: [
                { required: true, message: "Please enter new quantity" },
                {
                  validator: (rule, value, callback) => {
                    if (value < 0) {
                      callback("Quantity cannot be less than 0");
                    } else {
                      callback();
                    }
                  },
                },
              ],
            })(
              <InputNumber
                style={{ width: "100%" }}
                disabled={!editable}
                onChange={(val) => calculateAdjusted(val)}
              />
            )}
          </Form.Item>

          <Form.Item label="Quantity Adjusted">
            {getFieldDecorator("quantityAdjusted", {
              initialValue: 0,
            })(<InputNumber style={{ width: "100%" }} disabled />)}
          </Form.Item>

      <Form.Item label="Reason">
  {getFieldDecorator("reason")(
    <Input.TextArea 
      rows={3} 
      placeholder="Enter reason (optional)" 
      disabled={!editable}
      onKeyUp={(e) => {
        // Allow alphanumeric, spaces, hyphens, commas, dots, and forward slashes
        const value = e.target.value.replace(/[^a-zA-Z0-9\s\-.,/]/g, '');
        form.setFieldsValue({
          reason: value,
        });
      }}
      onInput={(event) => {
        // Filter out special characters in real-time
        const rawValue = event.target.value;
        const filteredValue = rawValue.replace(/[^a-zA-Z0-9\s\-.,/]/g, '');
        if (filteredValue !== rawValue) {
          event.target.value = filteredValue;
          form.setFieldsValue({
            reason: filteredValue,
          });
        }
      }}
      maxLength={500}
    />
  )}
</Form.Item>

          <Form.Item style={{ textAlign: "right", marginBottom: 0 }}>
            <Button onClick={close} style={{ marginRight: 8 }}>
              {editable ? "Cancel" : "Close"}
            </Button>
            {editable && (
              <Button type="primary" onClick={handleSubmit}>
                Submit
              </Button>
            )}
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default Form.create({ name: "spareAdjustmentForm" })(SpareAdjustmentForm);
